import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { Link } from "../router.jsx";
import { money, fmtDateShort, isoDate, today, addDays } from "../format.js";
import {
  Async, Badge, Button, Card, Check, Empty, ErrorPanel, Field, Fields, Input, Modal, Ribbon, Select,
  Table, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const FREQUENCIES = [
  { key: "week", label: "Every week" },
  { key: "month", label: "Every month" },
  { key: "quarter", label: "Every quarter" },
  { key: "year", label: "Every year" },
];

const freqLabel = (f) => FREQUENCIES.find((x) => x.key === f)?.label || `Every ${f}`;

/** The API replaces the whole row on PUT, so every write sends the full profile. */
const payloadOf = (p) => ({
  client_id: p.client_id,
  project_id: p.project_id || null,
  description: p.description || "",
  amount: Number(p.amount) || 0,
  taxable: p.taxable !== false,
  frequency: p.frequency || "month",
  next_issue_on: isoDate(p.next_issue_on) || null,
  auto_send: !!p.auto_send,
  status: p.status === "paused" ? "paused" : "active",
});

export default function Recurring() {
  const { settings, me } = useApp();
  const canManage = !!me.permissions["recurring.manage"];
  const toast = useToast();
  const state = useAsync(() => api.get("recurring"), []);
  const [editing, setEditing] = useState(null); // a profile, or {} for a new one
  const [busy, setBusy] = useState(null); // `${action}:${id}`
  const [error, setError] = useState(null);
  const sym = settings.currency_symbol;

  async function toggle(p) {
    setError(null);
    setBusy(`toggle:${p.id}`);
    try {
      const next = p.status === "active" ? "paused" : "active";
      await api.put(`recurring/${p.id}`, { ...payloadOf(p), status: next });
      toast.push(next === "paused" ? "Profile paused" : "Profile resumed");
      state.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function runNow(p) {
    setError(null);
    setBusy(`run:${p.id}`);
    try {
      const res = await api.post(`recurring/${p.id}/run`, {});
      if (!res.issued) toast.push(res.reason || "Nothing was issued", "err");
      else toast.push(`${res.issued.number} ${res.auto_sent ? "issued and emailed" : "created as a draft"}`);
      state.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <TopBar crumb="Get paid" title="Recurring invoices">
        {canManage && (
          <Button kind="pri" onClick={() => setEditing({})}>
            ＋ New recurring profile
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Ribbon>
          ↻ Recurring profiles generate an invoice automatically on the schedule you set. Turn on auto-send to skip
          approval.
        </Ribbon>
        {error && <ErrorPanel error={error} />}

        <Async state={state}>
          {(list) => (
            <>
              <Card bare className="mt">
                <Table
                  rows={list}
                  onRowClick={canManage ? (p) => setEditing(p) : undefined}
                  empty={<Empty icon="↻">No recurring profiles yet</Empty>}
                  columns={[
                    {
                      key: "client_name",
                      label: "Client",
                      render: (p) => (
                        <>
                          <b>{p.client_name}</b>
                          {p.project_name && <div className="tiny muted">{p.project_name}</div>}
                        </>
                      ),
                    },
                    {
                      key: "description",
                      label: "Description",
                      render: (p) => <span className="sm">{p.description || <span className="muted">—</span>}</span>,
                    },
                    { key: "frequency", label: "Frequency", render: (p) => <span className="sm dim">{freqLabel(p.frequency)}</span> },
                    {
                      key: "next_issue_on",
                      label: "Next issue",
                      render: (p) => (
                        <span className={`sm ${p.status === "active" ? "" : "muted"}`}>
                          {p.next_issue_on ? fmtDateShort(p.next_issue_on) : "—"}
                        </span>
                      ),
                    },
                    {
                      key: "amount",
                      label: "Amount",
                      align: "right",
                      render: (p) => <span className="num">{money(p.amount, sym)}</span>,
                    },
                    {
                      key: "auto_send",
                      label: "Auto-send",
                      render: (p) =>
                        p.auto_send ? <Badge status="paid" label="On" /> : <Badge status="draft" label="Off" />,
                    },
                    { key: "status", label: "Status", render: (p) => <Badge status={p.status} /> },
                    {
                      key: "actions",
                      label: "",
                      align: "right",
                      render: (p) => (
                        <div className="row" style={{ justifyContent: "flex-end", gap: 6 }}>
                          <Button
                            sm
                            kind="gho"
                            busy={busy === `run:${p.id}`}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              runNow(p);
                            }}
                          >
                            Run now
                          </Button>
                          <Button
                            sm
                            kind="gho"
                            busy={busy === `toggle:${p.id}`}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              toggle(p);
                            }}
                          >
                            {p.status === "active" ? "Pause" : "Resume"}
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </Card>

              <Card className="mt" title="Payment reminders">
                <div style={{ display: "grid", gap: 8 }} className="sm">
                  <div>
                    The client is emailed <b>{Number(settings.remind_before_days)} day(s) before</b> the due date.
                  </div>
                  <div>
                    Then <b>{Number(settings.remind_after_days)} day(s) after</b> the due date if it is still open.
                  </div>
                  <div>
                    A final notice goes out <b>{Number(settings.remind_final_days)} day(s) after</b> the due date.
                  </div>
                  <div>
                    Late fees:{" "}
                    {Number(settings.late_fee_percent) > 0 ? (
                      <b>{Number(settings.late_fee_percent)}% per month on overdue balances</b>
                    ) : (
                      <b>none</b>
                    )}
                    .
                  </div>
                  <div className="tiny muted">
                    Reminders run on a schedule for every open invoice, not just recurring ones. Change the timings in{" "}
                    <Link to="/settings" className="linky">
                      Settings → Reminders
                    </Link>
                    .
                  </div>
                </div>
              </Card>
            </>
          )}
        </Async>
      </Wrap>

      {editing && (
        <ProfileModal profile={editing} onClose={() => setEditing(null)} onSaved={state.reload} />
      )}
    </>
  );
}

/* -------------------------------------------------------------- profile modal */

function ProfileModal({ profile, onClose, onSaved }) {
  const { clients, projects } = useApp();
  const toast = useToast();
  const isNew = !profile.id;

  const [form, setForm] = useState({
    client_id: profile.client_id || "",
    project_id: profile.project_id || "",
    description: profile.description || "",
    amount: profile.amount ?? "",
    taxable: profile.taxable !== false,
    frequency: profile.frequency || "month",
    next_issue_on: isoDate(profile.next_issue_on) || addDays(today(), 1),
    auto_send: !!profile.auto_send,
    status: profile.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function run(fn, message) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      toast.push(message);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const save = () => {
    if (!form.client_id) return setError(new Error("Pick a client first."));
    return run(
      () => (isNew ? api.post("recurring", payloadOf(form)) : api.put(`recurring/${profile.id}`, payloadOf(form))),
      isNew ? "Recurring profile created" : "Recurring profile saved"
    );
  };

  return (
    <Modal
      title={isNew ? "New recurring profile" : "Edit recurring profile"}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button
              kind="dgr"
              busy={busy}
              style={{ marginRight: "auto" }}
              onClick={() => run(() => api.del(`recurring/${profile.id}`), "Recurring profile deleted")}
            >
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            {isNew ? "Create profile" : "Save profile"}
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}

        <Fields cols={2}>
          <Field label="Client">
            <Select
              value={form.client_id}
              onChange={(e) => {
                set("client_id", e.target.value);
                set("project_id", "");
              }}
            >
              <option value="">Select a client…</option>
              {clients
                .filter((c) => c.status === "active" || c.id === form.client_id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Project (optional)">
            <Select value={form.project_id} onChange={(e) => set("project_id", e.target.value)}>
              <option value="">None</option>
              {projects
                .filter((p) => p.client_id === form.client_id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </Select>
          </Field>
        </Fields>

        <Field label="Description" hint="This becomes the single line item on each invoice.">
          <Input
            value={form.description}
            placeholder="Managed hosting — monthly"
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <Fields cols={3}>
          <Field label="Amount">
            <Input value={form.amount} placeholder="0.00" onChange={(e) => set("amount", e.target.value)} />
          </Field>
          <Field label="Frequency">
            <Select value={form.frequency} onChange={(e) => set("frequency", e.target.value)}>
              {FREQUENCIES.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Next issue">
            <Input type="date" value={form.next_issue_on} onChange={(e) => set("next_issue_on", e.target.value)} />
          </Field>
        </Fields>

        <Check label="Taxable" checked={form.taxable} onChange={(v) => set("taxable", v)} />
        <Check
          label="Auto-send the invoice by email when it is issued"
          checked={form.auto_send}
          onChange={(v) => set("auto_send", v)}
        />
        <Check
          label="Paused — stop issuing invoices from this profile"
          checked={form.status === "paused"}
          onChange={(v) => set("status", v ? "paused" : "active")}
        />
      </Fields>
    </Modal>
  );
}
